import { useState } from 'react';
import { formatDate } from '../lib/format.js';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Chip,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SettingsIcon from '@mui/icons-material/Settings';
import { TicketRoutingDialog } from '../components/TicketRoutingDialog.js';
import { useMe, useProjects } from '../api/hooks.js';
import { api } from '../api/client.js';
import { STATUS_COLOR, PRIORITY_COLOR, type Ticket } from './ticketShared.js';
import { useT } from '../i18n/useT.js';

type FilterKey = 'all' | 'hasReplay' | 'hasRage' | 'hasError' | 'highFrustration';

const FILTERS: { key: FilterKey; icon?: JSX.Element }[] = [
  { key: 'all' },
  { key: 'hasReplay', icon: <PlayArrowIcon sx={{ fontSize: 15 }} /> },
  { key: 'hasRage', icon: <LocalFireDepartmentIcon sx={{ fontSize: 15 }} /> },
  { key: 'hasError' },
  { key: 'highFrustration' },
];

export function TicketsPage() {
  const t = useT().tickets;
  const navigate = useNavigate();
  const { data: me } = useMe();
  const orgId = me?.organizations[0]?.id;
  const canTest = !!me?.organizations[0]?.tester;
  const base = `/v1/orgs/${orgId}/tickets`;
  const [filter, setFilter] = useState<FilterKey>('all');
  const [search, setSearch] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [routingOpen, setRoutingOpen] = useState(false);

  const counts = useQuery({
    enabled: !!orgId,
    queryKey: ['ticket-counts', orgId],
    queryFn: async () => (await api.get(`${base}/counts`)).data as Record<string, number>,
  });

  const tickets = useQuery({
    enabled: !!orgId,
    queryKey: ['tickets', orgId, filter, search],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filter !== 'all') params.set(filter, '1');
      if (search) params.set('search', search);
      return (await api.get(`${base}?${params}`)).data.data as Ticket[];
    },
  });

  const rows = tickets.data ?? [];

  return (
    <Box>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="baseline" spacing={1}>
          <Typography variant="h5">{t.title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t.count(counts.data?.all ?? rows.length)}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<SettingsIcon />} onClick={() => setRoutingOpen(true)}>
            {t.configuration}
          </Button>
          {canTest && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
              {t.newTicket}
            </Button>
          )}
        </Stack>
      </Stack>

      <TicketRoutingDialog open={routingOpen} onClose={() => setRoutingOpen(false)} orgId={orgId ?? ''} />

      <TextField
        size="small"
        fullWidth
        placeholder={t.searchPlaceholder}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2 }}
      />

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        {FILTERS.map((f) => {
          const n = f.key === 'all' ? counts.data?.all : counts.data?.[f.key];
          const active = filter === f.key;
          return (
            <Chip
              key={f.key}
              icon={f.icon}
              label={`${t.filters[f.key]}${n != null ? `  ${n}` : ''}`}
              onClick={() => setFilter(f.key)}
              color={active ? 'primary' : 'default'}
              variant={active ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600 }}
            />
          );
        })}
      </Stack>

      <Paper sx={{ overflow: 'hidden' }}>
        <Stack divider={<Divider flexItem />}>
          {rows.map((tk) => (
            <Stack
              key={tk.id}
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ px: 2, py: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
              onClick={() => navigate(`/tickets/${tk.id}`)}
            >
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: PRIORITY_COLOR[tk.priority], flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="baseline">
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'ui-monospace, monospace' }}>
                    TK-{tk.number}
                  </Typography>
                  <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                    {tk.title}
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {[tk.customerName || tk.customerEmail, tk.customerOrg, formatDate(tk.createdAt)]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              </Box>

              <Stack direction="row" spacing={0.75} alignItems="center">
                {tk.sessionId && <Chip size="small" icon={<PlayArrowIcon sx={{ fontSize: 14 }} />} label={t.chips.replay} variant="outlined" />}
                {!!tk.signals.rageClicks && (
                  <Chip size="small" icon={<LocalFireDepartmentIcon sx={{ fontSize: 14, color: '#ef4444' }} />} label={tk.signals.rageClicks} sx={{ bgcolor: 'error.main', color: '#fff' }} />
                )}
                {tk.signals.frustrationScore != null && (
                  <Chip size="small" label={`${tk.signals.frustrationScore}%`} sx={{ bgcolor: frustrationBg(tk.signals.frustrationScore), color: '#fff' }} />
                )}
                {tk.signals.replaySource && <Chip size="small" icon={<AutoAwesomeIcon sx={{ fontSize: 13 }} />} label={t.chips.ai} variant="outlined" />}
                <Chip size="small" label={t.status[tk.status] ?? tk.status} sx={{ bgcolor: alphaBg(STATUS_COLOR[tk.status]), color: STATUS_COLOR[tk.status], fontWeight: 700 }} />
                <Box sx={{ width: 120, textAlign: 'right' }}>
                  {tk.assignee ? (
                    <Typography variant="caption" noWrap>{tk.assignee.name || tk.assignee.email}</Typography>
                  ) : (
                    <Typography variant="caption" color="text.disabled">{t.unassigned}</Typography>
                  )}
                </Box>
              </Stack>
            </Stack>
          ))}
          {rows.length === 0 && (
            <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
              {filter !== 'all' ? t.emptyFiltered : t.empty}
            </Typography>
          )}
        </Stack>
      </Paper>

      <NewTicketDialog
        open={addOpen}
        orgId={orgId}
        onClose={() => setAddOpen(false)}
        onCreated={(id) => {
          setAddOpen(false);
          navigate(`/tickets/${id}`);
        }}
      />
    </Box>
  );
}

function NewTicketDialog({
  open,
  orgId,
  onClose,
  onCreated,
}: {
  open: boolean;
  orgId?: string;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const all = useT();
  const t = all.tickets.dialog;
  const priorities = all.tickets.priority;
  const common = all.common;
  const qc = useQueryClient();
  const { data: projects } = useProjects(orgId);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('MEDIUM');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [projectId, setProjectId] = useState('');
  const [sessionId, setSessionId] = useState('');

  const create = useMutation({
    mutationFn: async () =>
      (
        await api.post(`/v1/orgs/${orgId}/tickets`, {
          title,
          description: description || undefined,
          priority,
          customerName: customerName || undefined,
          customerEmail: customerEmail || undefined,
          projectId: projectId || undefined,
          sessionId: sessionId || undefined,
        })
      ).data,
    onSuccess: (t: { id: string }) => {
      qc.invalidateQueries({ queryKey: ['tickets', orgId] });
      qc.invalidateQueries({ queryKey: ['ticket-counts', orgId] });
      onCreated(t.id);
    },
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t.title}</DialogTitle>
      <DialogContent>
        <TextField fullWidth margin="normal" label={t.ticketTitle} value={title} onChange={(e) => setTitle(e.target.value)} />
        <TextField fullWidth margin="normal" label={t.description} multiline minRows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        <Stack direction="row" spacing={2}>
          <TextField select margin="normal" label={t.priority} value={priority} onChange={(e) => setPriority(e.target.value)} sx={{ minWidth: 140 }}>
            {['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'].map((p) => (<MenuItem key={p} value={p}>{priorities[p] ?? p}</MenuItem>))}
          </TextField>
          <TextField margin="normal" label={t.customerName} value={customerName} onChange={(e) => setCustomerName(e.target.value)} fullWidth />
        </Stack>
        <TextField fullWidth margin="normal" label={t.customerEmail} value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} />
        <Divider sx={{ my: 1.5 }}><Typography variant="caption" color="text.secondary">{t.linkSession}</Typography></Divider>
        <Stack direction="row" spacing={2}>
          <TextField select margin="dense" label={t.project} value={projectId} onChange={(e) => setProjectId(e.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value="">{t.none}</MenuItem>
            {(projects ?? []).map((p) => (<MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>))}
          </TextField>
          <TextField margin="dense" label={t.sessionId} value={sessionId} onChange={(e) => setSessionId(e.target.value)} fullWidth placeholder={t.sessionPlaceholder} />
        </Stack>
        {create.isError && <Typography color="error" variant="body2" sx={{ mt: 1 }}>{t.failed}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{common.cancel}</Button>
        <Button variant="contained" disabled={!title.trim() || create.isPending} onClick={() => create.mutate()}>
          {create.isPending ? t.creating : t.create}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const alphaBg = (hex: string) => `${hex}22`;
function frustrationBg(score: number): string {
  if (score >= 70) return '#ef4444';
  if (score >= 40) return '#f59e0b';
  return '#64748b';
}
